import { useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { TONE_CLASS, TONE_LABEL } from '../lib/status.ts';
import type { StatusTone } from '../lib/status.ts';
import { EntityHoverCard } from './HoverCard.tsx';

export interface HiveCell {
  key: string;
  tone: StatusTone;
  /** Accessible name: "build-01, offline, 3 sessions". */
  label: string;
  /** A tiny glyph inside the cell, so status never relies on colour alone. */
  glyph: ReactNode;
  /** 0..1 fill from the bottom (load), optional. */
  fill?: number;
}

export interface HiveProps {
  cells: HiveCell[];
  onOpen: (key: string) => void;
  /** Hover card content for one cell. */
  card: (key: string) => ReactNode;
  activeKey?: string | null;
  /** Accessible name of the grid. */
  label: string;
  /** Legend: one entry per tone present, with counts. */
  legend?: { tone: StatusTone; label?: string; glyph: ReactNode }[];
}

/** Cell size: size-7 (1.75rem) plus gap-1 (0.25rem). */
const CELL_REM = 1.75;
const GAP_REM = 0.25;

/**
 * The dense "hive" view (DesignSystem 4.4): one cell per node, coloured by status with a
 * glyph, detail on hover, Enter opens. Arrow keys move across the grid.
 */
export function Hive({ cells, onOpen, card, activeKey, label, legend }: HiveProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [focus, setFocus] = useState(0);

  const columns = () => {
    const el = ref.current;
    if (!el) return 1;
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    return Math.max(1, Math.floor((el.clientWidth + GAP_REM * rem) / ((CELL_REM + GAP_REM) * rem)));
  };

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const cols = columns();
    const step: Record<string, number> = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: cols, ArrowUp: -cols, Home: -Infinity, End: Infinity };
    const d = step[e.key];
    if (d == null) return;
    e.preventDefault();
    const next = d === -Infinity ? 0 : d === Infinity ? cells.length - 1 : Math.min(cells.length - 1, Math.max(0, focus + d));
    setFocus(next);
    ref.current?.querySelectorAll<HTMLElement>('[data-hive-cell]')[next]?.focus();
  };

  const counts = new Map<StatusTone, number>();
  for (const c of cells) counts.set(c.tone, (counts.get(c.tone) ?? 0) + 1);

  return (
    <div className="flex flex-col gap-3">
      {legend && (
        <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-fg-muted" aria-label="Legend">
          {legend
            .filter((l) => counts.get(l.tone))
            .map((l) => (
              <li key={l.tone} className="flex items-center gap-1.5">
                <span className={cn('inline-flex size-4 items-center justify-center rounded-control border [&_svg]:size-2.5', TONE_CLASS[l.tone].soft, TONE_CLASS[l.tone].border, TONE_CLASS[l.tone].fg)}>
                  {l.glyph}
                </span>
                {l.label ?? TONE_LABEL[l.tone]}
                <span className="text-fg-subtle num">{counts.get(l.tone)}</span>
              </li>
            ))}
        </ul>
      )}
      <div ref={ref} role="group" aria-label={label} onKeyDown={onKey} className="grid gap-1" style={{ gridTemplateColumns: `repeat(auto-fill, ${CELL_REM}rem)` }}>
        {cells.map((c, i) => {
          const tc = TONE_CLASS[c.tone];
          const active = c.key === activeKey;
          return (
            <EntityHoverCard key={c.key} content={() => card(c.key)} openDelay={250}>
              <button
                type="button"
                data-hive-cell={c.key}
                aria-label={c.label}
                aria-current={active || undefined}
                tabIndex={i === focus ? 0 : -1}
                onFocus={() => setFocus(i)}
                onClick={() => onOpen(c.key)}
                className={cn(
                  'relative flex size-7 items-center justify-center overflow-hidden rounded-control border [&_svg]:relative [&_svg]:size-3',
                  tc.soft,
                  tc.border,
                  tc.fg,
                  // Hover marks the cell with a hairline ring; nothing grows or moves under the pointer.
                  active ? 'ring-2 ring-accent ring-offset-1 ring-offset-bg' : 'hover:ring-1 hover:ring-border-strong',
                )}
              >
                {c.fill != null && c.fill > 0 && (
                  <span aria-hidden data-fill="" className={cn('absolute inset-0 origin-bottom opacity-25 transition-transform duration-(--duration-slow) ease-settle', tc.bg)} style={{ transform: `scaleY(${Math.round(Math.min(1, c.fill) * 100) / 100})` }} />
                )}
                {c.glyph}
              </button>
            </EntityHoverCard>
          );
        })}
      </div>
    </div>
  );
}
